import React from 'react';
import Button from './ui/Button';
import { ScheduleSuggestion, ConflictDetail } from '../types/AutoScheduling';

interface SuggestionItemProps {
    suggestion: ScheduleSuggestion;
    onSelect: () => void;
    rank: number;
    disabled?: boolean;
}

/**
 * Individual suggestion display component with conflict breakdown and selection button
 */
const SuggestionItem: React.FC<SuggestionItemProps> = ({ suggestion, onSelect, rank, disabled = false }) => {
    const formatTime = (time: string) => {
        // Convert 24-hour format to 12-hour format for display
        const [hours, minutes] = time.split(':');
        if (!hours || !minutes) return time; // Return original if parsing fails
        const hour = parseInt(hours, 10);
        const ampm = hour >= 12 ? 'PM' : 'AM';
        const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
        return `${displayHour}:${minutes} ${ampm}`;
    };

    const formatDay = (day: string) => {
        const dayMap: { [key: string]: string } = {
            'monday': 'Mon',
            'tuesday': 'Tue',
            'wednesday': 'Wed',
            'thursday': 'Thu',
            'friday': 'Fri',
            'saturday': 'Sat',
            'sunday': 'Sun'
        };
        return dayMap[day.toLowerCase()] || day;
    };

    const getSeverityIcon = (severity: 'orange' | 'red') => {
        return severity === 'red' ? '🚫' : '⚠️';
    };

    const getSeverityColor = (severity: 'orange' | 'red') => {
        return severity === 'red' ? 'text-red-600' : 'text-orange-600';
    };

    const getScoreColor = (score: number) => {
        if (score === 0) return 'text-green-600 bg-green-50';
        if (score <= 2) return 'text-orange-600 bg-orange-50';
        return 'text-red-600 bg-red-50';
    };

    const getScoreLabel = (score: number) => {
        if (score === 0) return 'Optimal';
        if (score <= 2) return 'Good';
        return 'Conflicts';
    };

    return (
        <div className={`border rounded-lg p-4 mb-3 transition-all duration-200 hover:shadow-md ${suggestion.isOptimal
            ? 'border-green-200 bg-green-50/30'
            : suggestion.conflictScore <= 2
                ? 'border-orange-200 bg-orange-50/30'
                : 'border-red-200 bg-red-50/30'
            }`}>
            {/* Header with rank and score */}
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                    <span className="text-lg font-semibold text-gray-700">#{rank}</span>
                    <div className={`px-2 py-1 rounded-full text-sm font-medium ${getScoreColor(suggestion.conflictScore)}`}>
                        Score: {suggestion.conflictScore} ({getScoreLabel(suggestion.conflictScore)})
                    </div>
                </div>
                <Button
                    onClick={onSelect}
                    variant={suggestion.isOptimal ? 'primary' : 'secondary'}
                    size="sm"
                    className="min-w-[80px]"
                    disabled={disabled}
                    title={disabled
                        ? 'Please wait while applying another suggestion'
                        : `Select this suggestion for ${formatDay(suggestion.day)} ${formatTime(suggestion.timeStart)}-${formatTime(suggestion.timeEnd)}`
                    }
                    aria-label={disabled
                        ? 'Selection disabled while applying another suggestion'
                        : `Select suggestion ${rank}: ${formatDay(suggestion.day)} ${formatTime(suggestion.timeStart)}-${formatTime(suggestion.timeEnd)} in ${suggestion.room}`
                    }
                >
                    {disabled ? 'Please wait...' : suggestion.isOptimal ? '✓ Select' : 'Select'}
                </Button>
            </div>

            {/* Main suggestion details */}
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-4">
                    <div className="text-lg font-semibold text-gray-900">
                        {formatDay(suggestion.day)}
                    </div>
                    <div className="text-lg text-gray-700">
                        {formatTime(suggestion.timeStart)} - {formatTime(suggestion.timeEnd)}
                    </div>
                    <div className="text-lg text-gray-700">
                        Room {suggestion.room}
                    </div>
                </div>
                {suggestion.weeks && suggestion.weeks.length > 1 ? (
                    <div className="text-sm text-gray-500">
                        Weeks {suggestion.weeks.join(', ')}
                    </div>
                ) : suggestion.week ? (
                    <div className="text-sm text-gray-500">
                        Week {suggestion.week}
                    </div>
                ) : null}
            </div>

            {/* Conflict details */}
            {suggestion.conflicts.length > 0 ? (
                <div className="space-y-2">
                    <div className="text-sm font-medium text-gray-600 mb-2">
                        Conflicts:
                    </div>
                    <div className="space-y-1">
                        {suggestion.conflicts.map((conflict, index) => (
                            <div
                                key={index}
                                className={`flex items-center gap-2 text-sm ${getSeverityColor(conflict.severity)}`}
                            >
                                <span className="text-base" role="img" aria-label={conflict.severity === 'red' ? 'Critical conflict' : 'Minor conflict'}>
                                    {getSeverityIcon(conflict.severity)}
                                </span>
                                <span className="flex-1">
                                    {conflict.description}
                                </span>
                                <span className="text-xs font-medium">
                                    +{conflict.points} pts
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            ) : (
                <div className="flex items-center gap-2 text-sm text-green-600">
                    <span className="text-base" role="img" aria-label="No conflicts">✅</span>
                    <span>No conflicts - Perfect match!</span>
                </div>
            )}
        </div>
    );
};

export default SuggestionItem;